import Notification from '@rio-cloud/rio-uikit/Notification';
import Button from 'components/actions/Button';
import ErrorDialog from 'components/overlays/ErrorDialog';
import { SystemAdminDialogTypes } from 'helper/types';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDeleteUserByIdMutation, useListUsersQuery, useResetPasswordByIdMutation } from 'services/user';
import {
    getActiveUser,
    getSystemAdminOpenDialog,
    setActiveUser,
    setSystemAdminOpenDialog,
} from 'store/slices/settingsPageSlice';

import SystemTableHeader from '../common/SystemTableHeader';
import type { SystemTableColumn } from '../common/tableColumn';
import { getNextSortConfig, type SortConfig, sortItems } from '../common/tableSorting';
import NoContent from '../NoContent';
import TableError from '../TableError';
import TableSkeleton from '../TableSkeleton';
import DeleteUserDialog from './DeleteUserDialog';
import ResetPasswordDialog from './ResetPasswordDialog';
import SystemUsersRow from './SystemUsersRow';
import { getUserFullName } from './utils';

import type { User } from 'API';

import './_styles.scss';

type SortKey = 'firstName' | 'lastName' | 'email' | 'phone' | 'country' | 'city' | 'status' | 'createdAt' | 'updatedAt';
type TableColumnKey = SortKey | 'teams' | 'roles' | '_';

const getColumns = (openDialogInfo: () => void): SystemTableColumn<TableColumnKey, SortKey, User>[] => [
    { key: 'firstName', label: 'First name', sortable: true },
    { key: 'lastName', label: 'Last name', sortable: true },
    { key: 'email', label: 'E-mail', sortable: true },
    { key: 'phone', label: 'Phone', sortable: true },
    { key: 'country', label: 'Country', sortable: true },
    { key: 'city', label: 'City', sortable: true },
    {
        key: 'teams',
        label: 'Team',
    },
    {
        key: 'roles',
        label: (
            <>
                Role
                <span
                    className='rioglyph rioglyph-info-sign text-15 ml-5 cursor-pointer text-gray-dark'
                    onClick={openDialogInfo}
                />
            </>
        ),
    },
    { key: 'status', label: 'Status', sortable: true },
    { key: 'createdAt', label: 'Created', sortable: true, sortValue: user => new Date(user.createdAt ?? '') },
    { key: 'updatedAt', label: 'Updated', sortable: true, sortValue: user => new Date(user.updatedAt ?? '') },
    { key: '_', label: '' },
];

interface Props {
    openDialogInfo: () => void;
}

const SystemUsersTable = ({ openDialogInfo }: Props) => {
    const dispatch = useDispatch();
    const activeUser = useSelector(getActiveUser);
    const systemAdminOpenDialog = useSelector(getSystemAdminOpenDialog);
    const resetPasswordErrorDialogIsOpen = systemAdminOpenDialog === SystemAdminDialogTypes.RESET_PASSWORD_ERROR;
    const deleteUserErrorDialogIsOpen = systemAdminOpenDialog === SystemAdminDialogTypes.DELETE_USER_ERROR;
    const [resetUserPassword, { data: resetPasswordData, isLoading: isResetPasswordLoading }] =
        useResetPasswordByIdMutation({
            fixedCacheKey: 'resetPasswordById',
        });
    const [deleteUserById, { data: deleteUserData, isLoading: isDeleteUserLoading }] = useDeleteUserByIdMutation({
        fixedCacheKey: 'deleteUserById',
    });

    const {
        data: systemUsers,
        isLoading: isSystemUsersLoading,
        isFetching: isSystemUsersFetching,
        isError,
        refetch,
    } = useListUsersQuery('ListUsers', {
        refetchOnMountOrArgChange: true,
    });

    const columns = useMemo(() => getColumns(openDialogInfo), [openDialogInfo]);

    const [sortConfig, setSortConfig] = useState<SortConfig<SortKey>>({ key: 'firstName', dir: 'asc' });
    const sortedUsers = useMemo(() => {
        if (!systemUsers) return [];
        return sortItems(systemUsers, sortConfig, columns);
    }, [systemUsers, sortConfig, columns]);

    const closeDialog = () => {
        dispatch(setSystemAdminOpenDialog(undefined));
    };

    const closeFlow = () => {
        const fullName = getUserFullName(activeUser) ?? 'user';
        closeDialog();
        Notification.success(<p>They’ll find instructions in the email to create a new password</p>, {
            title: `Password reset email sent to ${fullName}`,
            timeout: 10_000,
            closeButton: true,
            hideProgressBar: false,
        });
    };

    const closeDeleteFlow = (deletedUser: User) => {
        const fullName = getUserFullName(deletedUser) ?? 'user';
        dispatch(setActiveUser(undefined));
        closeDialog();
        Notification.success(<p>The user will be logged out within 5 minutes if currently logged in</p>, {
            title: `${fullName} has been deleted`,
            timeout: 10_000,
            closeButton: true,
            hideProgressBar: false,
        });
    };

    const refreshTable = async () => {
        await refetch()
            .unwrap()
            .then(() => closeFlow());
    };

    const submitResetPassword = async () => {
        if (!activeUser) {
            console.error('No user selected');
            return;
        }

        await resetUserPassword({ input: activeUser.id })
            .unwrap()
            .then(res => {
                if (res && 'errorCode' in res) {
                    throw new Error(`Error code found: ${res.errorCode}`);
                }

                if (res.statusCode !== 200) {
                    throw new Error('Error status code');
                }

                refreshTable();
            })
            .catch(error => {
                console.error(error);
                dispatch(setSystemAdminOpenDialog(SystemAdminDialogTypes.RESET_PASSWORD_ERROR));
            });
    };

    const submitDeleteUser = async () => {
        if (!activeUser) {
            console.error('No user selected');
            return;
        }

        await deleteUserById({ input: activeUser.id })
            .unwrap()
            .then(res => {
                if (res && 'errorCode' in res) {
                    throw new Error(`Error code found: ${res.errorCode}`);
                }

                if (res.statusCode !== 204) {
                    throw new Error('Error status code');
                }

                closeDeleteFlow(activeUser);
            })
            .catch(error => {
                console.error(error);
                dispatch(setSystemAdminOpenDialog(SystemAdminDialogTypes.DELETE_USER_ERROR));
            });
    };

    if (isSystemUsersLoading) {
        return <TableSkeleton id='users-table' tableHeaders={Object.fromEntries(columns.map(c => [c.key, c.label]))} />;
    }

    if (!sortedUsers.length) {
        return (
            <NoContent
                title='No users yet, but they will appear here'
                body='As of now, there are no users in the Control Center. Add a user to assign roles and add to teams.'
                button={
                    <Button data-testid='no-content-users-button' style='primary' disabled>
                        <span className='rioglyph rioglyph-plus' />
                        Create new user
                    </Button>
                }
            />
        );
    }

    if (isError) {
        return (
            <div className='TableContentWrapper'>
                <TableError header="An error occured while fetching users' data" />
            </div>
        );
    }

    return (
        <div>
            <table
                data-testid='exceptions-table'
                className='table-head-filled table-sticky-in-container table-hover table-bordered table'
            >
                <SystemTableHeader<TableColumnKey, SortKey, User>
                    columns={columns}
                    sortConfig={sortConfig}
                    onSort={colKey => setSortConfig(prev => getNextSortConfig(prev, colKey))}
                    headerRowClassName='SystemUsersTable__header'
                    headerCellClassName='SystemUsersTable__columnHeader'
                />
                <tbody className='SystemUsersTable__body'>
                    {sortedUsers.map(_ => (
                        <SystemUsersRow key={_.email} user={_} />
                    ))}
                </tbody>
            </table>
            <ResetPasswordDialog
                onConfirm={submitResetPassword}
                onCancel={closeDialog}
                isLoading={isSystemUsersFetching || isResetPasswordLoading}
            />
            <DeleteUserDialog
                onConfirm={submitDeleteUser}
                onCancel={closeDialog}
                isLoading={isSystemUsersFetching || isDeleteUserLoading}
            />
            <ErrorDialog
                show={resetPasswordErrorDialogIsOpen}
                onConfirm={closeDialog}
                errorStatus={resetPasswordData?.statusCode}
            />
            <ErrorDialog
                show={deleteUserErrorDialogIsOpen}
                onConfirm={closeDialog}
                errorStatus={deleteUserData?.statusCode}
            />
        </div>
    );
};

export default SystemUsersTable;
